
import React from 'react';

interface EligibilityHeroProps {
  score: number;
  status: 'eligible' | 'conditional' | 'not-eligible';
  city: string;
}

export function EligibilityHero({ score, status, city }: EligibilityHeroProps) {
  const statusConfig = {
    eligible: {
      label: 'Likely Eligible',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      icon: 'ri-checkbox-circle-fill',
      iconColor: 'text-emerald-500',
      ringColor: 'ring-emerald-500',
      progressColor: 'bg-emerald-500',
      message: `Great news! Based on your answers, your property in ${city} appears to meet the key requirements for short-term rental hosting on Airbnb.`,
    },
    conditional: {
      label: 'Conditionally Eligible',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'ri-error-warning-fill',
      iconColor: 'text-amber-500',
      ringColor: 'ring-amber-500',
      progressColor: 'bg-amber-500',
      message: `Your property in ${city} may be eligible for Airbnb hosting, but there are some areas that need attention before you can legally operate.`,
    },
    'not-eligible': {
      label: 'Not Currently Eligible',
      color: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'ri-close-circle-fill',
      iconColor: 'text-red-500',
      ringColor: 'ring-red-500',
      progressColor: 'bg-red-500',
      message: `Based on your answers, your property in ${city} does not currently meet the requirements for short-term rental hosting. But don’t worry — there may still be options.`,
    },
  } as const;

  // Defensive programming: ensure we have a valid config
  const config = statusConfig[status];
  if (!config) {
    console.error(`Invalid status "${status}" supplied to EligibilityHero.`);
    return null;
  }

  // Determine stroke colour based on status (fallback to a neutral colour)
  const strokeColor =
    status === 'eligible'
      ? '#10B981'
      : status === 'conditional'
      ? '#F59E0B'
      : '#EF4444';

  // Guard against out‑of‑range scores
  const safeScore = Math.min(Math.max(score, 0), 100);
  const dashArray = `${(safeScore / 100) * 327} 327`;

  return (
    <section className="pt-12 pb-16">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <div className="mb-8">
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${config.bg} ${config.border} border mb-6`}
          >
            <i className={`${config.icon} ${config.iconColor} text-lg`}></i>
            <span className={`text-sm font-semibold ${config.color}`}>{config.label}</span>
          </div>

          <h1 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-4">
            Your Airbnb Eligibility Results
          </h1>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed max-w-2xl mx-auto">
            {config.message}
          </p>
        </div>

        {/* Score Ring */}
        <div className="flex justify-center mb-8">
          <div className="relative w-40 h-40">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="52" fill="none" stroke="#F0F0F0" strokeWidth="10" />
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke={strokeColor}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={dashArray}
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-4xl font-bold ${config.color}`}>{safeScore}</span>
              <span className="text-xs text-[#505A5A]/50 font-medium">out of 100</span>
            </div>
          </div>
        </div>

        <p className="text-sm text-[#505A5A]/50">
          This is an automated preliminary assessment based on publicly available regulations. It is not
          legal advice.
        </p>
      </div>
    </section>
  );
}
